import type { ChordShareDirection } from '@revelio/replots';

import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import type { TransitionMetricValues } from '../../PageConfigProvider/types/metric';
import type { SingleDataPoint, TransitionBreakdownData } from '../types';

export type ChordMatrixData = {
  matrix: number[][];
  labels: string[];
  shareMatrix: number[][];
  shareDirection: ChordShareDirection;
};

type SelectedEntity = {
  id?: string;
  name?: string;
};

const parseEntityNumericId = (id?: string | null): number | null => {
  if (!id) return null;
  return getNumericIdFromNestedId(id);
};

const resolveFlowIndex = (
  flow: { dim: string; id?: string | null },
  indexByNumericId: Map<number, number>,
  dimToIndex: Map<string, number>
): number | undefined => {
  const flowNumericId = parseEntityNumericId(flow.id);
  if (flowNumericId !== null) {
    return indexByNumericId.get(flowNumericId);
  }
  return dimToIndex.get(flow.dim);
};

type DirectedFlow = { flow: SingleDataPoint; isInflow: boolean };

/**
 * Pick the flow list that should populate the chord matrix for one entity.
 *
 * - Single-direction metrics (inflow OR outflow) populate `breakdown` and the
 *   direction comes from the requested metric.
 * - The combined `transitions_inflows_and_outflows` metric leaves `breakdown`
 *   empty and splits the data into `outflowBreakdown` / `inflowBreakdown`.
 *   We prefer outflows so when both
 *   endpoints of a pair are selected we don't double-count the same edge
 *   (each pair would otherwise be seen as both A's outflow to B and B's
 *   inflow from A). When outflows are missing we fall back to inflows so
 *   single-source data still renders.
 */
const collectEntityMatrixFlows = (
  entityRow: TransitionBreakdownData,
  metric: TransitionMetricValues
): DirectedFlow[] => {
  if (entityRow.breakdown && entityRow.breakdown.length > 0) {
    const isInflow = metric === 'transitions_inflow';
    return entityRow.breakdown.map((flow) => ({ flow, isInflow }));
  }
  if (entityRow.outflowBreakdown && entityRow.outflowBreakdown.length > 0) {
    return entityRow.outflowBreakdown.map((flow) => ({
      flow,
      isInflow: false,
    }));
  }
  return (entityRow.inflowBreakdown ?? []).map((flow) => ({
    flow,
    isInflow: true,
  }));
};

const resolveShareDirection = (
  metric: TransitionMetricValues
): ChordShareDirection =>
  metric === 'transitions_inflow' ? 'inflows' : 'outflows';

/**
 * Convert transition flow data into a square matrix suitable for a chord
 * diagram. For inflow data, `breakdown.dim` represents the source entity and
 * for outflow data it represents the target entity.
 *
 * Entities are matched by numeric id (not display name) so the chord works
 * when primary-filter labels (`long_name`) differ from API `short_name`.
 * Only flows between entities present in `selectedEntities` are included.
 */
export const convertTransitionDataToChordMatrix = ({
  data,
  selectedEntities,
  metric,
}: {
  data: TransitionBreakdownData[];
  selectedEntities: SelectedEntity[];
  metric: TransitionMetricValues;
}): ChordMatrixData => {
  const entityByNumericId = new Map<number, TransitionBreakdownData>();
  data.forEach((entityRow) => {
    const numericId = parseEntityNumericId(entityRow.id);
    if (numericId !== null) {
      entityByNumericId.set(numericId, entityRow);
    }
  });

  const orderedNumericIds = (() => {
    const fromSelection = selectedEntities
      .map((entity) => parseEntityNumericId(entity.id))
      .filter((id): id is number => id !== null)
      .filter((id, index, ids) => ids.indexOf(id) === index)
      .filter((id) => entityByNumericId.has(id));

    if (fromSelection.length > 0) return fromSelection;

    return data
      .map((entityRow) => parseEntityNumericId(entityRow.id))
      .filter((id): id is number => id !== null);
  })();

  const labels = orderedNumericIds.map((numericId) => {
    const entityRow = entityByNumericId.get(numericId);
    return entityRow?.dim ?? String(numericId);
  });

  const indexByNumericId = new Map(
    orderedNumericIds.map((numericId, idx) => [numericId, idx])
  );
  const dimToIndex = new Map(
    orderedNumericIds.map((numericId, idx) => [
      entityByNumericId.get(numericId)?.dim ?? '',
      idx,
    ])
  );

  const matrix: number[][] = labels.map(() =>
    new Array<number>(labels.length).fill(0)
  );
  const shareMatrix: number[][] = labels.map(() =>
    new Array<number>(labels.length).fill(0)
  );

  orderedNumericIds.forEach((entityNumericId) => {
    const entityRow = entityByNumericId.get(entityNumericId);
    const entityIdx = indexByNumericId.get(entityNumericId);
    if (!entityRow || entityIdx === undefined) return;

    collectEntityMatrixFlows(entityRow, metric).forEach(
      ({ flow, isInflow }) => {
        const flowIdx = resolveFlowIndex(flow, indexByNumericId, dimToIndex);
        if (flowIdx === undefined) return;

        const value = flow.value ?? 0;
        if (value <= 0) return;

        const sourceIdx = isInflow ? flowIdx : entityIdx;
        const targetIdx = isInflow ? entityIdx : flowIdx;
        const row = matrix[sourceIdx];
        const shareRow = shareMatrix[sourceIdx];
        if (!row || !shareRow) return;
        row[targetIdx] = (row[targetIdx] ?? 0) + value;
        // Pass the API share through as-is — a row-sum-derived share would
        // only reflect the selected entities, not the real out/inflow total.
        shareRow[targetIdx] = (shareRow[targetIdx] ?? 0) + (flow.share ?? 0);
      }
    );
  });

  return {
    matrix,
    labels,
    shareMatrix,
    shareDirection: resolveShareDirection(metric),
  };
};

/**
 * For each selected entity, find the highest-value breakdown partner that is
 * NOT itself a selected entity. Used as the chord empty state's "add one of
 * these entities to see transitions" suggestions so the user has a concrete
 * recovery path instead of just an error message.
 *
 * Partners matched via numeric id when available, falling back to display
 * name. Duplicates are removed (multiple selected entities can share the same
 * top external partner).
 */
export const computeChordEmptyStateSuggestions = ({
  data,
  selectedEntities,
}: {
  data: TransitionBreakdownData[];
  selectedEntities: SelectedEntity[];
}): string[] => {
  const selectedNumericIds = new Set(
    selectedEntities
      .map((entity) => parseEntityNumericId(entity.id))
      .filter((id): id is number => id !== null)
  );
  const selectedNames = new Set(
    selectedEntities
      .map((entity) => entity.name)
      .filter((name): name is string => Boolean(name))
  );

  const isPartnerSelected = (partner: {
    dim: string;
    id?: string | null;
  }): boolean => {
    const partnerNumericId = parseEntityNumericId(partner.id);
    if (partnerNumericId !== null && selectedNumericIds.has(partnerNumericId)) {
      return true;
    }
    return selectedNames.has(partner.dim);
  };

  const suggestions: string[] = [];
  for (const entityRow of data) {
    // Combine all partner flows (single-direction `breakdown` AND the
    // split `inflowBreakdown` / `outflowBreakdown` used by the combined
    // inflows-and-outflows metric). Pick the single highest-value external
    // partner — direction doesn't matter for "who would create traffic if
    // added to the selection".
    const allFlows: SingleDataPoint[] = [
      ...(entityRow.breakdown ?? []),
      ...(entityRow.outflowBreakdown ?? []),
      ...(entityRow.inflowBreakdown ?? []),
    ];
    const topExternalPartner = allFlows.reduce<SingleDataPoint | undefined>(
      (best, partner) => {
        if (isPartnerSelected(partner)) return best;
        if ((partner.value ?? 0) <= 0) return best;
        if (!best) return partner;
        return (partner.value ?? 0) > (best.value ?? 0) ? partner : best;
      },
      undefined
    );

    if (topExternalPartner?.dim) {
      suggestions.push(topExternalPartner.dim);
    }
  }

  return Array.from(new Set(suggestions));
};
